import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import DownloadIcon from "@/assets/campaign/FFFFFF_purchase_icon.svg?react";
import ImageDollarIcon from "@/assets/colorable/image_dollar.svg?react";
import RightArrow from "@/assets/colorable/right_arrow.svg?react";
import Button from "@/components/Common/Button";
import useContentDownload from "@/hooks/useContentDownload";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useIsAdmin} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {useCampaignGalleryStore} from "@/store/campaignGallery";
import useCampaignManagementStore, {useManagementCampaign} from "@/store/campaignManagement";
import {TCarouselMedia} from "@/store/carousel";
import {isMoreBuyoutsModalOpenAtom, purchaseModalContentAtom} from "@/store/jotaiAtoms";
import {useQueryClient} from "@tanstack/react-query";
import {useSetAtom} from "jotai";
import {FC, memo, useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";

const ContentPurchaseButton: FC<{
  className?: string;
  contents: TCarouselMedia[];
  buyoutsPerContent?: number | null;
}> = ({contents, className, buyoutsPerContent = 1}) => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();
  const campaignId = Number(useManagementCampaign().id);
  const searchParams = useCampaignGalleryStore(state => state.searchParams);
  const setMoreBuyoutsModalOpen = useSetAtom(isMoreBuyoutsModalOpenAtom);
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);
  const isAdmin = useIsAdmin();
  const clearCalculatedGalleryImages = useCampaignGalleryStore(state => state.clearCalculatedImages);
  const determineHasPurchased = useCallback(
    () =>
      isAdmin ||
      contents[0].instagram_media?.media_product_type === "UGC" ||
      !!contents[0].instagram_media?.purchased ||
      false,
    [contents],
  );
  const [hasPurchased, setHasPurchased] = useState<boolean>(determineHasPurchased());
  const onPurchased = useCallback(() => {
    setHasPurchased(true);
    clearCalculatedGalleryImages();
    [
      CAMPAIGN_KEYS.qCampaignReportingApplications({campaignId, dateFilter}).slice(0, 3),
      CAMPAIGN_KEYS.qCampaignGallery(campaignId, searchParams).slice(0, 3),
    ].map(queryKey => queryClient.refetchQueries({queryKey: queryKey}));
  }, [setHasPurchased]);
  useEffect(() => {
    if (!hasPurchased) setHasPurchased(determineHasPurchased());
  }, [contents, determineHasPurchased]);
  const setPurchaseContent = useSetAtom(purchaseModalContentAtom);
  const {remainingBuyouts} = usePackageLimits();
  const hasEnoughBuyouts = useMemo(() => remainingBuyouts > 0, [remainingBuyouts]);
  const {downloadMedia, isDownloading} = useContentDownload(contents, contents[0].campaign?.title);

  if (hasPurchased) {
    return (
      <Button
        className={clsxm("gap-2", className)}
        shouldScaleOnHover={false}
        buttonStyle="Blue"
        isLoading={isDownloading}
        isDisabled={isDownloading}
        onClick={downloadMedia}>
        <DownloadIcon className="z-10" />
        <span className="z-10">{t("downloadContent")}</span>
      </Button>
    );
  }

  return (
    <div className="flex items-end">
      <div className="border-primary-light-solid text-neutral-black bg-neutral-grey-lightest flex h-12 items-center justify-center gap-1 rounded-l-md border px-3">
        <ImageDollarIcon className="size-3 shrink-0" />
        <span className="text-sm font-semibold">{buyoutsPerContent}</span>
      </div>
      <Button
        className={clsxm("h-12 gap-2 rounded-l-none", className)}
        shouldScaleOnHover={false}
        buttonStyle="Blue"
        onClick={() => {
          if (hasEnoughBuyouts) {
            setPurchaseContent({buyoutsPerContent: buyoutsPerContent!, contents, onPurchased});
          } else {
            setMoreBuyoutsModalOpen({show: true, type: "EXTRA_BUYOUTS"});
          }
        }}>
        <span className="z-10 text-wrap">{t(hasEnoughBuyouts ? "useBuyout" : "purchaseBuyouts")}</span>
        <RightArrow className="z-10 text-white" />
      </Button>
    </div>
  );
};

export default memo(ContentPurchaseButton);
